import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as ToastPrimitives from "@radix-ui/react-toast";
import type { Toast as UiToast } from '../../../reducers/uiReducer';
import { setToast } from '../../../reducers/uiReducer';
import type { RootState } from '../../../store/store';
import cn from "classnames";
import "./toast.css";
import styles from "./Toast.module.scss";

export const useToast = () => {
    const dispatch = useDispatch();

    const triggerToast = (toast: UiToast) => {
        dispatch(setToast(toast));
        setTimeout(() => {
            dispatch(setToast(null));
        }, 3000);
    }

    return { triggerToast };
}

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed bottom-0 right-0 z-50 flex max-h-screen w-full flex-col-reverse p-4 sm:max-w-[420px]",
      className
    )}
    {...props}
  />
))

const Toast = () => {
    const toast = useSelector((state: RootState) => state.ui.toast);
    if (!toast) {
        return null;
    }

    return (
        <ToastPrimitives.Provider>
            <ToastPrimitives.Root
                className={cn(
                    "group relative flex w-full items-center justify-between space-x-4 rounded-md bg-white p-4 shadow-lg",
                    "data-[state=open]:animate-in data-[state=closed]:animate-out",
                    "data-[state=closed]:fade-out-80 data-[state=open]:fade-in-80",
                    "data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full",
                    "ToastRoot"
                )}
                open={true}
            >
                {/* UI-DESIGN: the title and message must flow vertically;
                    their named grid areas overlapped inside the old inner grid. */}
                <div className={styles.content}>
                    {toast.title && (
                        <ToastPrimitives.Title className="ToastTitle text-sm font-semibold">
                            {toast.title}
                        </ToastPrimitives.Title>
                    )}
                    {toast.description && (
                        <ToastPrimitives.Description className="ToastDescription text-sm opacity-90">
                            {toast.description}
                        </ToastPrimitives.Description>
                    )}
                </div>
                <ToastPrimitives.Close className="absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none">
                    ✕
                </ToastPrimitives.Close>
            </ToastPrimitives.Root>
            <ToastViewport />
        </ToastPrimitives.Provider>
    )
}

export default Toast;
